import { useEffect, useMemo, useState } from "react";
import { ChevronDown, HelpCircle, LifeBuoy, Search, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";

import { EmptyPanel, PageHero } from "@/components/interior-page";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { resourceAnchorId, useResourceHighlight } from "@/hooks/useResourceHighlight";
import {
  fetchPublishedResources,
  fetchResourceCategories,
  type ResourceCategory,
  type ResourceContent,
} from "@/lib/resources";

/**
 * Frequently asked questions. Content is admin-managed — see
 * frontend/src/pages/AdminResourceContent.tsx. Uses the same expand/collapse
 * treatment as the plan FAQ on /choose-plan: one panel open at a time, chevron
 * rotates, no extra library.
 */
export default function FaqPage() {
  const [faqs, setFaqs] = useState<ResourceContent[] | null>(null);
  const [categories, setCategories] = useState<ResourceCategory[]>([]);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    fetchPublishedResources("faq")
      .then(setFaqs)
      .catch(() => setFailed(true));
    // Only used to order the sections. A failed fetch is not fatal: the
    // questions still render, grouped in the order they arrive.
    fetchResourceCategories("faq")
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  const { targetId, highlightedId } = useResourceHighlight(faqs !== null);

  // A deep-linked question opens with its answer showing.
  useEffect(() => {
    if (faqs && targetId) setOpenId(targetId);
  }, [faqs, targetId]);

  const groups = useMemo(() => {
    if (!faqs) return [];
    const q = query.trim().toLowerCase();
    const matches = q
      ? faqs.filter(
          (f) =>
            f.title.toLowerCase().includes(q) ||
            (f.body ?? "").toLowerCase().includes(q) ||
            (f.category ?? "").toLowerCase().includes(q),
        )
      : faqs;

    // Grouped by the managed category when one is set, else by the legacy
    // free-text category (rows that predate the backfill). Questions keep the
    // API's admin-defined order inside each group.
    const byCategory = new Map<string, { label: string; items: ResourceContent[] }>();
    for (const f of matches) {
      const label = f.category?.trim() || "";
      const key = f.category_id ?? `text:${label.toLowerCase()}`;
      const bucket = byCategory.get(key);
      if (bucket) bucket.items.push(f);
      else byCategory.set(key, { label, items: [f] });
    }

    // Sections follow the order the admin arranged the categories in. Anything
    // not in that list (legacy text, a deactivated category, no category) keeps
    // its first-appearance order after them.
    const rank = new Map(categories.map((c, i) => [c.id, i]));
    return [...byCategory.entries()]
      .map(([key, group], appearance) => ({
        key,
        ...group,
        sort: rank.get(key) ?? categories.length + appearance,
      }))
      .sort((a, b) => a.sort - b.sort);
  }, [faqs, categories, query]);

  return (
    <div className="public-stage pb-8">
      <div className="w-full px-4 py-8 sm:px-6 lg:px-8">
        {/* Shared Dark · Gold interior hero — same header as AI Deal
            Matching and the rest of the interior pages. */}
        <div className="mb-8">
          <PageHero
            eyebrow="Resource Library"
            icon={HelpCircle}
            title="Frequently Asked"
            titleAccent="Questions"
            description={"Answers to the questions we're asked most about buying and selling on the platform."}
          />
        </div>

        {faqs && faqs.length > 0 && (
          <div className="mb-8 w-full sm:max-w-md">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search questions..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
        )}

        {failed ? (
          <EmptyPanel
            icon={TriangleAlert}
            title="Couldn't load the FAQ"
            description="Something went wrong fetching these questions. Please refresh the page to try again."
          />
        ) : faqs === null ? (
          <div className="flex justify-center py-12"><Loader /></div>
        ) : groups.length === 0 ? (
          <EmptyPanel
            icon={HelpCircle}
            title={faqs.length === 0 ? "No questions published yet" : "No matching questions"}
            description={
              faqs.length === 0
                ? "Answers will appear here once they're published."
                : "Nothing here matches that search. Try a shorter phrase, or open a support ticket and we'll answer directly."
            }
          />
        ) : (
          <div className="space-y-8">
            {groups.map(({ key, label, items }) => (
              <section key={key}>
                {label && (
                  <h2 className="mb-3 text-[0.65rem] font-bold uppercase tracking-wide text-primary">
                    {label}
                  </h2>
                )}
                <div className="space-y-3">
                  {items.map((faq) => {
                    const open = openId === faq.id;
                    return (
                      <div
                        key={faq.id}
                        id={resourceAnchorId(faq.id)}
                        className={`scroll-mt-24 overflow-hidden rounded-2xl border transition duration-700 ${
                          highlightedId === faq.id
                            ? "border-primary bg-primary/10 shadow-md ring-2 ring-primary/60"
                            : "border-border bg-card"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setOpenId(open ? null : faq.id)}
                          aria-expanded={open}
                          className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                        >
                          <span className="text-sm font-semibold text-foreground sm:text-base">
                            {faq.title}
                          </span>
                          <ChevronDown
                            className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
                          />
                        </button>
                        {open && (
                          <p className="whitespace-pre-wrap px-5 pb-5 text-sm leading-7 text-muted-foreground">
                            {faq.body}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Anything the FAQ doesn't cover goes to the help desk. /support is the
            subscription-agnostic route, so it stays reachable for every role. */}
        <div className="mt-8 flex flex-col items-center justify-center gap-3 rounded-2xl border border-border bg-card px-5 py-5 text-center sm:flex-row sm:text-left">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <LifeBuoy className="h-4 w-4 text-primary" />
            Still have a question we haven't answered?
          </div>
          <Link
            to="/support"
            className="text-sm font-semibold text-primary underline-offset-2 hover:underline"
          >
            Contact support
          </Link>
        </div>
      </div>
    </div>
  );
}
